import { useEffect, useState } from "react";
import { ArrowDownToLine, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { api, errMsg, feeOf, fmtUsd, MIN_FEE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { WithdrawCodeStep } from "@/components/WithdrawCodeStep";
import { SavedAccountPicker } from "@/components/SavedAccountPicker";

const FIELDS = [
  { k: "account_holder", l: "Account holder name", p: "Jane Doe" },
  { k: "bank_name", l: "Bank name", p: "Chase" },
  { k: "account_number", l: "Account number / IBAN", p: "000123456789" },
];
const inputCls = "w-full rounded-xl border border-[#212638] bg-[#0F111A] px-4 py-2.5 text-sm outline-none transition-colors focus:border-[#F7931A]";

export const WithdrawDialog = ({ onDone }) => {
  const { user, setUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ amount: "", account_holder: "", bank_name: "", account_number: "" });
  const [busy, setBusy] = useState(false);
  const bal = user?.usd_balance || 0;
  const amt = Math.round((parseFloat(form.amount) || 0) * 100) / 100;
  const [pending, setPending] = useState(null);
  const [saved, setSaved] = useState([]);
  const [accountId, setAccountId] = useState("new");
  const [saveNew, setSaveNew] = useState(true);
  useEffect(() => {
    if (!open) return;
    api.get("/bank-accounts").then((r) => { setSaved(r.data); setAccountId(r.data[0]?.account_id || "new"); }).catch(() => {});
  }, [open]);
  const useNew = accountId === "new";
  const trusted = !useNew && saved.find((a) => a.account_id === accountId)?.trusted;
  const error = amt > bal + 1e-9 ? "Insufficient USD balance" : amt > 0 && amt <= MIN_FEE ? "Amount must be more than the $0.01 minimum fee" : "";
  const valid = amt >= 0.01 && !error && (!useNew || (form.account_holder.trim().length >= 2 && form.bank_name.trim().length >= 2 && form.account_number.replace(/\s/g, "").length >= 4));
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    setBusy(true);
    try {
      const dest = useNew
        ? { account_holder: form.account_holder, bank_name: form.bank_name, account_number: form.account_number, save_account: saveNew }
        : { account_id: accountId };
      const { data } = await api.post("/withdrawals", { usd_amount: amt, ...dest });
      if (data.completed) {
        confirmed(data);
      } else {
        setPending({ ...data, amount: amt });
        toast.success("Verification code sent to your email");
      }
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };

  const confirmed = (data) => {
    setUser(data.user);
    toast.success(`Withdrawal of ${fmtUsd(data.transaction.usd_amount)} approved and paid`);
    setForm({ amount: "", account_holder: "", bank_name: "", account_number: "" });
    setPending(null);
    setOpen(false);
    onDone?.();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setPending(null); }}>
      <DialogTrigger asChild>
        <button data-testid="withdraw-open-button" className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-[#212638] px-3 py-1.5 text-xs font-semibold text-[#94A3B8] transition-colors hover:bg-[#1C2030] hover:text-white">
          <ArrowDownToLine size={13} /> Withdraw
        </button>
      </DialogTrigger>
      <DialogContent className="border-[#212638] bg-[#151824] text-[#F8FAFC]" data-testid="withdraw-dialog">
        <DialogHeader>
          <DialogTitle className="tracking-tight">Withdraw USD</DialogTitle>
          <DialogDescription className="text-[#94A3B8]">Instant bank payout. Available: <span className="font-mono text-emerald-400" data-testid="withdraw-available">{fmtUsd(bal)}</span></DialogDescription>
        </DialogHeader>
        {pending ? <WithdrawCodeStep pending={pending} onBack={() => setPending(null)} onConfirmed={confirmed} /> : (
        <div className="space-y-3">
          <div>
            <div className="flex gap-2">
              <input value={form.amount} onChange={set("amount")} type="number" min="0" step="0.01" placeholder="Amount (USD)" data-testid="withdraw-amount-input" className={`${inputCls} font-mono`} />
              <button onClick={() => setForm({ ...form, amount: bal.toFixed(2) })} data-testid="withdraw-max-button" className="rounded-xl border border-[#212638] px-4 text-xs font-semibold text-[#94A3B8] transition-colors hover:bg-[#1C2030] hover:text-white">Max</button>
            </div>
            {error && <p className="mt-1.5 text-xs text-red-400" data-testid="withdraw-error">{error}</p>}
            {amt > 0 && (
              <p className="mt-1.5 flex justify-between text-xs text-[#94A3B8]" data-testid="withdraw-fee-preview">
                <span>Fee (1%, min $0.01): <span className="font-mono">{fmtUsd(feeOf(amt))}</span></span>
                <span>You receive: <span className="font-mono text-emerald-400">{fmtUsd(Math.max(0, amt - feeOf(amt)))}</span></span>
              </p>
            )}
          </div>
          {saved.length > 0 && <SavedAccountPicker accounts={saved} value={accountId} onChange={setAccountId} />}
          {useNew && (
            <>
              {FIELDS.map((f) => (
                <input key={f.k} value={form[f.k]} onChange={set(f.k)} placeholder={f.l} aria-label={f.l} data-testid={`withdraw-${f.k.replace(/_/g, "-")}-input`} className={inputCls} />
              ))}
              <label className="flex cursor-pointer items-center gap-2 text-xs text-[#94A3B8]">
                <input type="checkbox" checked={saveNew} onChange={(e) => setSaveNew(e.target.checked)} data-testid="withdraw-save-account-checkbox" className="h-4 w-4 accent-[#F7931A]" />
                Save this bank account to my profile
              </label>
            </>
          )}
          <button onClick={submit} disabled={!valid || busy} data-testid="withdraw-submit-button"
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#F7931A] py-3 font-semibold text-[#08090E] transition-colors hover:bg-amber-500 disabled:opacity-50">
            {busy && <Loader2 className="animate-spin" size={16} />} {trusted ? "Withdraw now" : "Continue"}
          </button>
          <p className="text-center text-xs text-[#64748B]" data-testid="withdraw-verify-hint">{trusted ? "Trusted account — no email code needed. Paid instantly." : "We'll email you a code to confirm. Withdrawals are then paid instantly."}</p>
        </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
